import { defineStore } from "pinia";
import { useNuxtApp } from "#app";
import { useMerchantApi } from "~/composables/useMerchantApi.js";
import { useContext } from "~/stores/context.js";
import { useEmployeeSession } from "~/stores/employeeSession.js";

export const useAuth = defineStore("auth", {
  state: () => ({
    token: useCookie(
      useRuntimeConfig().public.cookiePrefix + "terminal_token",
      {
        maxAge: 60 * 60 * 24 * 7,
      },
    ),
  }),

  actions: {
    async login(merchantId, terminalId) {
      const runtimeConfig = useRuntimeConfig();

      const token = useCookie(runtimeConfig.public.cookiePrefix + "jwt_token");

      const response = await useMerchantApi(
        merchantId,
        "/terminals/" + terminalId + "/tokens",
        { method: "POST" },
      );

      this.token = response.data.attributes.value;

      token.value = null;

      await this.initialize();
    },

    async initialize() {
      const context = useContext();

      if (!this.token) {
        throw new Error("No token present");
      }

      if (context.id) {
        return;
      }

      await this.refresh();

      setInterval(() => this.refresh(), 10 * 60 * 1000);

      await this.loadContext();

      this.listen();
    },

    async refresh() {
      const runtimeConfig = useRuntimeConfig();

      try {
        const response = await $fetch(
          runtimeConfig.public.apiUrl + "/api/v1/auth/tokens/refresh",
          {
            method: "POST",
            headers: { Authorization: "Bearer " + this.token },
          },
        );

        this.token = response.data.attributes.value;
      } catch (error) {
        if (error.statusCode === 401) {
          // We know the token is expired
          this.token = null;

          this.context = null;

          await navigateTo("/setup");
        }
      }
    },

    async loadContext() {
      const runtimeConfig = useRuntimeConfig();
      const contextStore = useContext();
      const employeeSession = useEmployeeSession();

      const response = await $fetch(
        runtimeConfig.public.apiUrl + "/api/v1/terminal/context",
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.token,
          },
        },
      );

      contextStore.setContext(response.data);

      employeeSession.setSession(response.data.attributes.employee_session);
    },

    async getMerchantAccesses() {
      const runtimeConfig = useRuntimeConfig();
      const token = useCookie(runtimeConfig.public.cookiePrefix + "jwt_token");

      return await $fetch(
        runtimeConfig.public.apiUrl + "/api/v1/auth/merchant-accesses",
        {
          headers: {
            Accept: "application/json",
            ContentType: "application/json",
            Authorization: "Bearer " + token.value,
          },
        },
      ).then((response) => response.data);
    },

    async changeTerminal(id) {
      const runtimeConfig = useRuntimeConfig();

      const response = await $fetch(
        runtimeConfig.public.apiUrl + "/api/v1/terminal/tokens",
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + this.token,
          },
          method: "POST",
          body: {
            terminal_id: id,
          },
        },
      );

      this.token = response.data.attributes.value;

      await this.loadContext();

      this.listen();
    },

    listen() {
      const nuxtApp = useNuxtApp();
      const context = useContext();

      nuxtApp.$echo
        .private("terminals." + context.id)
        .notification((notification) => {
          context.increaseUnreadNotifications();

          switch (notification.type) {
            case "print-job-failed":
              return useNotifications().error("A print job failed.");
            case "late-reservation-created":
              return useNotifications().success(
                "A late reservation has been created.",
              );
            case "stock-status":
              return useNotifications().success(
                "It is time for making a stock status.",
              );
          }
        });
    },
  },
});
